import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { posthog } from "posthog-js";
import { ButtonModule } from "primeng/button";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputText } from "primeng/inputtext";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { Tooltip } from "primeng/tooltip";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  finalize,
  map,
  merge,
  Observable,
  of,
  scan,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from "rxjs";
import { AccessRoleColorSeverityMapping } from "../../../../domain/access-role";
import { InvitationDetails } from "../../../../domain/invitation";
import { OrganizationDetails, SelfOrganization } from "../../../../domain/organization";
import { isInvitationsRouteData, isUserRouteData } from "../../../../domain/route-data";
import { UserDetails } from "../../../../domain/user";
import { ResponsibilityKind } from "../../../../domain/user-responsibility";
import { ConnectDialogService } from "../../../../services/connect-dialog.service";
import { InvitationsService } from "../../../../services/invitations.service";
import { OrganizationService } from "../../../../services/organization.service";
import { ToastService } from "../../../../services/toast.service";
import { UserService } from "../../../../services/user.service";
import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";
import { RanDatePipe } from "../../../../shared/pipes/ran-date.pipe";
import { reportToSentry } from "../../../../shared/sentry/reportFunctions";
import { PrimeNgSeverity } from "../../../../shared/types/primeng";

interface PendingInvitationViewModel extends InvitationDetails {
  loading: boolean;
}

type InvitationListAction = { type: "set"; list: PendingInvitationViewModel[] } | { type: "delete"; id: number };

@Component({
  selector: "app-organizations-select",
  standalone: true,
  imports: [
    ...primeNgPageModules,
    AsyncPipe,
    ButtonModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    TableModule,
    TagModule,
    TranslocoDirective,
    RanDatePipe,
    Tooltip,
    InputText,
  ],
  providers: [],
  templateUrl: "./select.page.html",
  styleUrl: "./select.page.scss",
})
export class OrganizationsSelectPage {
  public orgSearchInputValue$ = new BehaviorSubject<string>("");
  public loading = true;

  private refreshOrgListTrigger$ = new Subject<void>();
  public orgList$: Observable<SelfOrganization[]>;
  public filteredOrgList$: Observable<SelfOrganization[]>;

  private removeInvitationTrigger$ = new Subject<number>();
  private refreshInvitationsTrigger$ = new Subject<void>();
  public pendingInvitations$: Observable<PendingInvitationViewModel[] | null>;

  // Notification counts to be displayed on the organization list
  public notificationCountByOrg$: Observable<Map<number, number>>;

  public isUserInternal = false;
  private currentUser: UserDetails | null = null;

  constructor(
    private router: Router,
    private translocoService: TranslocoService,
    private userService: UserService,
    private invitationsService: InvitationsService,
    private readonly toastService: ToastService,
    private route: ActivatedRoute,
    private destroyRef: DestroyRef,
    private organizationService: OrganizationService,
    private connectDialogService: ConnectDialogService,
  ) {
    this.loading = true;
    this.notificationCountByOrg$ = this.userService.getSelfNotificationCountByOrg$();

    const routeData = this.route.snapshot.data;
    if (isUserRouteData(routeData)) {
      this.currentUser = routeData.user;
      this.isUserInternal = routeData.user.isInternal;
    }

    // Clear any previously selected organization when opening the select page
    this.organizationService.clearOrganization();

    let currentInvitations: InvitationDetails[] = [];
    if (isInvitationsRouteData(routeData)) {
      currentInvitations = routeData.invitations;
    }

    const refreshableCachedInvitations = this.refreshInvitationsTrigger$.pipe(
      startWith(currentInvitations),
      switchMap(() => this.invitationsService.getPendingInvitations$()),
      map((x) => x.map((y) => ({ ...y, loading: false }))),
    );

    this.pendingInvitations$ = merge(
      // Listen for both updates from the Invitations Service...
      refreshableCachedInvitations.pipe(map((list) => ({ type: "set" as const, list }))),
      // ...and removal requests from the removeInvitationTrigger subject up above.
      this.removeInvitationTrigger$.pipe(map((id) => ({ type: "delete" as const, id }))),
    ).pipe(
      scan<InvitationListAction, PendingInvitationViewModel[]>((acc, action) => {
        switch (action.type) {
          case "set":
            return action.list;
          case "delete":
            return acc.filter((inv) => inv.id !== action.id);
          default:
            return acc;
        }
      }, []),
      // Coerce empty list to null, so the @if statement hides the invitations panel
      map((invitations) => {
        if (invitations?.length === 0) {
          return null;
        } else {
          return invitations;
        }
      }),
    );

    this.orgList$ = this.refreshOrgListTrigger$.pipe(
      startWith([]),
      tap(() => (this.loading = true)),
      switchMap(() => this.userService.getSelfOrganizations$(true)),
      shareReplay(1),
    );

    this.filteredOrgList$ = combineLatest([this.orgList$, this.orgSearchInputValue$]).pipe(
      catchError(() => {
        return of([[] as SelfOrganization[], "" as string] as const);
      }),
      switchMap((x) => {
        const [orgList, filterString] = x;
        if (!filterString.trim()) {
          return of(orgList);
        }

        return of(orgList.filter((org) => org.name.toLowerCase().includes(filterString.toLowerCase())));
      }),
    );

    this.orgList$.pipe(takeUntilDestroyed()).subscribe({
      next: (organizations) => {
        this.loading = false;

        // Check query params for a tosDialog parameter
        const tosDialogParam = this.route.snapshot.queryParamMap.get("tosDialog");
        if (tosDialogParam) {
          const organizationId = Number(tosDialogParam);
          if (isNaN(organizationId)) {
            this.router.navigate(["organizations", "select"], {
              queryParams: { tosDialog: undefined },
            });
            return;
          }
          const organization = organizations.find((org) => org.id === organizationId);
          if (organization) {
            this.handleTosDialog(organization, (accepted) => {
              if (accepted) {
                this.router
                  .navigate(["organizations", organization.id], {
                    queryParams: { tosDialog: undefined },
                  })
                  .then(
                    (success) => {
                      if (!success) {
                        reportToSentry(
                          "Navigation failed after TOS acceptance via query param",
                          "Organizations.TosFlow",
                          {
                            level: "warning",
                            extra: {
                              organizationId: organization.id,
                              organizationName: organization.name,
                              currentUrl: this.router.url,
                              targetUrl: `/organizations/${organization.id}`,
                              queryParam: tosDialogParam,
                            },
                          },
                        );
                      }
                    },
                    (error) => {
                      reportToSentry(error, "Organizations.TosFlow", {
                        extra: {
                          context: "Navigation after TOS acceptance via query param",
                          organizationId: organization.id,
                          currentUrl: this.router.url,
                        },
                      });
                    },
                  );
              } else {
                // Remove the tosDialog parameter from the URL
                const rawParams = new URLSearchParams(window.location.search);
                rawParams.delete("tosDialog");
                const newUrlString = `${window.location.pathname}${rawParams.toString() ? "?" + rawParams.toString() : ""}`;
                window.history.replaceState(null, "", newUrlString);
              }
            });
          }
        }
      },
      error: (error) => {
        this.toastService.error(
          this.translocoService.translate("app.common.errors.loadMultipleOrganizations", { error: error?.message }),
        );
        this.loading = false;
      },
    });
  }

  /**
   * Handles the TOS dialog flow for an organization that hasn't accepted TOS yet.
   * @param organization The organization that needs TOS acceptance
   * @param onComplete Callback to execute after TOS handling is complete (parameter indicates if TOS was accepted)
   */
  private handleTosDialog(organization: SelfOrganization, onComplete?: (accepted: boolean) => void): void {
    const canAcceptTos =
      this.currentUser?.roleAssignments
        .find((ra) => ra.organizationId === organization.id)
        ?.accessRole.has("canAcceptTos") ?? false;

    const transformedOrg = OrganizationDetails.fromSelfOrganization(organization);
    const dialogRef = this.connectDialogService.displayOrganizationTosDialog(transformedOrg, canAcceptTos, false);

    if (!dialogRef) {
      this.organizationService.clearOrganization();
      onComplete?.(false);
      return;
    }

    dialogRef.onClose
      .pipe(
        take(1),
        switchMap((result) => {
          if (result?.accepted) {
            // User accepted TOS
            return this.organizationService.acceptTos$(organization.id).pipe(
              tap(() => {
                // Refresh org list to get updated tosAcceptedAt
                this.refreshOrgListTrigger$.next();
              }),
              map(() => true),
              catchError(() => {
                this.toastService.error(this.translocoService.translate("app.common.errors.acceptTos"));
                return of(false);
              }),
            );
          } else {
            // User rejected or closed the dialog
            this.organizationService.clearOrganization();
            return of(false);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (accepted) => {
          onComplete?.(accepted);
        },
        error: () => {
          this.organizationService.clearOrganization();
          onComplete?.(false);
        },
      });
  }

  /**
   * When an organization is selected, update the store and redirect to the organization page.
   * @param organization The organization that was selected.
   */
  public selectOrganization(organization: SelfOrganization): void {
    try {
      // Check if TOS needs to be accepted
      if (organization.tosAcceptedAt === null && !this.isUserInternal) {
        this.handleTosDialog(organization, (accepted) => {
          if (accepted) {
            this.router.navigate(["organizations", organization.id]).then(
              (success) => {
                if (!success) {
                  reportToSentry("Navigation failed after TOS acceptance", "Organizations.TosFlow", {
                    level: "warning",
                    extra: {
                      organizationId: organization.id,
                      organizationName: organization.name,
                      tosAcceptedAt: organization.tosAcceptedAt,
                      currentUrl: this.router.url,
                      targetUrl: `/organizations/${organization.id}`,
                    },
                  });
                }
              },
              (error) => {
                reportToSentry(error, "Organizations.TosFlow", {
                  extra: {
                    context: "Navigation after TOS acceptance",
                    organizationId: organization.id,
                    organizationName: organization.name,
                    currentUrl: this.router.url,
                    targetUrl: `/organizations/${organization.id}`,
                  },
                });
              },
            );
          }
        });
        return;
      }

      this.router.navigate(["organizations", organization.id]).then((success) => {
        if (!success) {
          reportToSentry("Navigation to organization failed", "Organizations.Select", {
            level: "warning",
            extra: {
              organizationId: organization.id,
              currentUrl: this.router.url,
            },
          });
        }
      });
    } catch (error) {
      reportToSentry(error, "Organizations.Select", {
        extra: {
          context: "selectOrganization method",
          organizationId: organization.id,
        },
      });
      this.router.navigateByUrl("organizations");
    }
  }

  /**
   * Trigger a request to get new invites. Does not skip cache.
   */
  public refreshInvites(): void {
    this.refreshInvitationsTrigger$.next();
  }

  public acceptInvitation(invitation: PendingInvitationViewModel): void {
    invitation.loading = true;
    this.invitationsService
      .acceptInvitation$(invitation.id)
      .pipe(
        // Refetch the self-user so the new role assignment is visible to elements gated by access role permissions
        switchMap(() => this.userService.refreshSelf$()),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (invitation.loading = false)),
      )
      .subscribe({
        next: (freshUser) => {
          this.currentUser = freshUser;
          this.removeInvitationTrigger$.next(invitation.id);
          this.refreshOrgListTrigger$.next();

          posthog.capture("invitation_accepted");
        },
        error: (e) => {
          reportToSentry(e, "Organizations.Invitation");
          this.toastService.error(
            this.translocoService.translate("app.organization-selection.invitations.acceptFailed"),
          );
        },
      });
  }

  public rejectInvitation(invitation: PendingInvitationViewModel): void {
    invitation.loading = true;
    this.invitationsService
      .rejectInvitation$(invitation.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (invitation.loading = false)),
      )
      .subscribe({
        next: () => {
          this.removeInvitationTrigger$.next(invitation.id);
          posthog.capture("invitation_rejected");
        },
        error: (e) => {
          reportToSentry(e, "Organizations.Invitation");
          this.toastService.error(
            this.translocoService.translate("app.organization-selection.invitations.rejectFailed"),
          );
        },
      });
  }

  public getResponsibilitySeverity(responsibility: ResponsibilityKind): PrimeNgSeverity {
    switch (responsibility) {
      case ResponsibilityKind.CustomerOverall: {
        return "warn";
      }
      case ResponsibilityKind.ProjectPrimary:
      case ResponsibilityKind.ProjectSecondary: {
        return "info";
      }
      case ResponsibilityKind.ManagerPrimary:
      case ResponsibilityKind.ManagerSecondary: {
        return "success";
      }
    }
  }

  // https://jsverse.gitbook.io/transloco/tools/keys-manager-tkm/keys-extractor#dynamic-keys
  /**
   * t(app.organization-selection.responsibilityTag.customerOverall)
   * t(app.organization-selection.responsibilityTag.projectPrimary)
   * t(app.organization-selection.responsibilityTag.projectSecondary)
   * t(app.organization-selection.responsibilityTag.managerPrimary)
   * t(app.organization-selection.responsibilityTag.managerSecondary)
   */
  public getResponsibilityTranslationKey(responsibility: ResponsibilityKind): string {
    const responsibilityString = responsibility.charAt(0).toLowerCase() + responsibility.slice(1);
    return `app.organization-selection.responsibilityTag.${responsibilityString}`;
  }

  public getAccessRoleColorSeverity(color: string | null): PrimeNgSeverity {
    if (color && color in AccessRoleColorSeverityMapping) {
      return AccessRoleColorSeverityMapping[color as keyof typeof AccessRoleColorSeverityMapping] ?? "info";
    }
    return "info";
  }
}
